import React from 'react';
import { IconButton, Badge, Box, keyframes, useTheme } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';

interface NotificationBadgeProps {
  count?: number;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  showAnimation?: boolean;
}

// Define the pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Ripple animation for the notification icon
const ripple = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(2.4);
    opacity: 0;
  }
`;

const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  count = 0,  // Provide default value to avoid undefined
  onClick,
  showAnimation = false
}) => {
  const theme = useTheme();

  // Determine badge display value
  const badgeContent = count > 99 ? '99+' : count;

  // Only show badge if count is greater than 0
  const showBadge = count > 0;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Ripple effect when new notifications arrive */}
      {showAnimation && showBadge && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            animation: `${ripple} 1.5s ease-out infinite`,
            border: `2px solid ${theme.palette.primary.main}`,
            borderRadius: '50%'
          }}
        />
      )}
      
      <IconButton
        onClick={onClick}
        size="small"
        sx={{
          animation: showAnimation && showBadge ? `${pulse} 1s ease-in-out` : 'none',
          p: { xs: 0.5, sm: 1 },
        }}
      >
        <Badge
          badgeContent={badgeContent}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              right: -3,
              top: 3,
              border: `2px solid ${theme.palette.background.paper}`,
              padding: '0 4px',
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              minWidth: { xs: 16, sm: 20 },
              height: { xs: 16, sm: 20 },
            }
          }}
        >
          <NotificationsIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </Badge>
      </IconButton>
    </Box>
  );
};

export default NotificationBadge;
